import { Component } from '@theme/component';
import { isFavorite, toggleFavorite } from './favorites-store.js';

/**
 * Heart toggle that saves product handle + optional variant id
 * (needed for color-split collection cards).
 *
 * @extends Component<{ button: HTMLButtonElement }>
 */
export class ProductFavoriteButton extends Component {
  requiredRefs = ['button'];

  /** @type {(() => void) | null} */
  #onChange = null;

  connectedCallback() {
    super.connectedCallback();
    this.#onChange = () => this.#sync();
    document.addEventListener('favorites:change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#onChange) {
      document.removeEventListener('favorites:change', this.#onChange);
      this.#onChange = null;
    }
  }

  toggle() {
    const handle = this.#handle;
    if (!handle) return;

    toggleFavorite(handle, this.#variantId);
    this.#sync();
  }

  /** @returns {string | undefined} */
  get #handle() {
    return this.dataset.productHandle;
  }

  /** @returns {string | null} */
  get #variantId() {
    const fromDataset = this.dataset.variantId;
    if (fromDataset) return fromDataset;

    const card = this.closest('product-card');
    const fromCard = card instanceof HTMLElement ? card.dataset.initialVariantId : null;
    return fromCard || null;
  }

  #sync() {
    const handle = this.#handle;
    const active = handle ? isFavorite(handle, this.#variantId) : false;

    this.refs.button.classList.toggle('product-favorite__trigger--active', active);
    this.refs.button.setAttribute('aria-pressed', String(active));
  }
}

if (!customElements.get('product-favorite-button')) {
  customElements.define('product-favorite-button', ProductFavoriteButton);
}
